import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { signOut } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebaseConfig";
import adminNavigation, { adminNavItems } from "./navigation";
import FeedbackProvider from "./components/FeedbackProvider";

const DRAWER_WIDTH = 248;

// The old panel kept its current section in a useState, so the browser back
// button, a refresh and a bookmark all dropped the admin back on the tutor
// list. Sections are routes now; this shell just renders whichever one the
// URL names.
function AdminLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { pathname } = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountEl, setAccountEl] = useState(null);
  const [email, setEmail] = useState("");

  useEffect(() => onAuthStateChanged(auth, (user) => setEmail(user?.email ?? "")), []);

  // Close the mobile drawer whenever navigation happens, or it stays over the
  // page the admin just asked for.
  useEffect(() => setMobileOpen(false), [pathname]);

  const current = adminNavItems.find((item) =>
    item.end ? pathname === item.path : pathname.startsWith(item.path)
  );

  const handleLogout = async () => {
    setAccountEl(null);
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#123358" }}>
      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.2 }}>
          A1 Home Tutors
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.75rem" }}>
          Admin panel
        </Typography>
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

      <Box sx={{ flexGrow: 1, overflowY: "auto", py: 1 }}>
        {adminNavigation.map((group) => (
          <List
            key={group.section}
            dense
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  color: "rgba(255,255,255,0.45)",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  lineHeight: 2.6,
                }}
              >
                {group.section}
              </ListSubheader>
            }
          >
            {group.items.map(({ key, label, path, icon: Icon, end }) => (
              <ListItemButton
                key={key}
                component={NavLink}
                to={path}
                end={end}
                sx={{
                  mx: 1.5,
                  mb: 0.5,
                  borderRadius: 1.5,
                  color: "rgba(255,255,255,0.78)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                  "&.active": {
                    bgcolor: "#f5a524",
                    color: "#123358",
                    fontWeight: 700,
                    "& .MuiListItemIcon-root": { color: "#123358" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: "inherit" }}
                />
              </ListItemButton>
            ))}
          </List>
        ))}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
      <Box sx={{ p: 2 }}>
        <ListItemButton
          component={Link}
          to="/"
          sx={{ borderRadius: 1.5, color: "rgba(255,255,255,0.78)" }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="View public site"
            primaryTypographyProps={{ fontSize: "0.8rem" }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <FeedbackProvider>
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f4f6fa" }}>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
            ml: { lg: `${DRAWER_WIDTH}px` },
            bgcolor: "#fff",
            color: "#1b2a41",
            borderBottom: "1px solid #e3e8f0",
          }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ display: { lg: "none" }, color: "inherit" }}
              aria-label="Open navigation"
            >
              <MenuIcon />
            </IconButton>

            <Typography sx={{ flexGrow: 1, fontWeight: 700, fontSize: "1.05rem" }}>
              {current?.label ?? "Admin"}
            </Typography>

            <Tooltip title={email || "Signed in"}>
              <IconButton onClick={(event) => setAccountEl(event.currentTarget)} size="small">
                <Avatar sx={{ width: 34, height: 34, bgcolor: "#174276", fontSize: "0.9rem" }}>
                  {(email[0] ?? "A").toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={accountEl}
              open={Boolean(accountEl)}
              onClose={() => setAccountEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography sx={{ fontSize: "0.7rem", color: "#7b809a" }}>Signed in as</Typography>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  {email || "Unknown account"}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ fontSize: "0.875rem", gap: 1 }}>
                <LogoutIcon fontSize="small" /> Log out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box component="nav" sx={{ width: { lg: DRAWER_WIDTH }, flexShrink: { lg: 0 } }}>
          <Drawer
            variant={isDesktop ? "permanent" : "temporary"}
            open={isDesktop ? true : mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": {
                width: DRAWER_WIDTH,
                boxSizing: "border-box",
                border: "none",
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
            minWidth: 0,
          }}
        >
          <Toolbar />
          <Stack sx={{ p: { xs: 2, md: 3 } }}>
            <Outlet />
          </Stack>
        </Box>
      </Box>
    </FeedbackProvider>
  );
}

export default AdminLayout;
